import * as Y from 'yjs';

import { UpdateMessageOrigin } from '@canvas/protocol-types';
import {
  CanvasTransactionOrigin,
  type TransactionOrigin,
  YJS_DEFAULT,
  type Transaction,
  type YjsOrigin as YjsOriginInterface,
} from '@canvas/transaction-types';

const CanvasTransactionOriginValues = new Set(Object.values(CanvasTransactionOrigin));

export const isTransactionOrigin = (val: any): val is TransactionOrigin => {
  return val === YJS_DEFAULT || val === undefined || isCanvasTransactionOrigin(val);
};

const isUndoManager = (origin: TransactionOrigin) => {
  return (
    origin instanceof Y.UndoManager ||
    CanvasTransactionOrigin.RemoteUndoManager === origin
  );
};

const isResizing = (origin: TransactionOrigin) => {
  return CanvasTransactionOrigin.Resizing === origin;
};

const isPrivateSessionManager = (origin: TransactionOrigin) => {
  return CanvasTransactionOrigin.PrivateSessionManager === origin;
};

const isAgent = (origin: TransactionOrigin) => {
  return (
    CanvasTransactionOrigin.Agent === origin ||
    CanvasTransactionOrigin.AgentImmediate === origin
  );
};

const isCanvasTransactionOrigin = (origin: string): origin is CanvasTransactionOrigin => {
  return CanvasTransactionOriginValues.has(origin as CanvasTransactionOrigin);
};
export const originIsUndoManager = (origin: TransactionOrigin) => {
  const isYjsOriginUndoManager =
    origin instanceof YjsOrigin &&
    (isUndoManager(origin.remoteOrigin) || isUndoManager(origin.localOrigin));
  return isUndoManager(origin) || isYjsOriginUndoManager;
};

export const originIsCleanDocument = (origin: TransactionOrigin) => {
  return CanvasTransactionOrigin.CleanDocument === origin;
};

export const originIsResizing = (origin: TransactionOrigin) => {
  const isYjsOriginResizing =
    origin instanceof YjsOrigin &&
    (isResizing(origin.remoteOrigin) || isResizing(origin.localOrigin));
  return isResizing(origin) || isYjsOriginResizing;
};

export const originIsAgent = (origin: TransactionOrigin) => {
  return isAgent(origin);
};

export const originIsImmediate = (origin: TransactionOrigin) => {
  return CanvasTransactionOrigin.AgentImmediate === origin;
};

export function getUpdateMessageOrigin(origin: TransactionOrigin): UpdateMessageOrigin {
  let flag = UpdateMessageOrigin.None;

  if (originIsUndoManager(origin)) {
    flag |= UpdateMessageOrigin.UndoRedo;
  } else if (originIsCleanDocument(origin)) {
    flag |= UpdateMessageOrigin.CleanDocument;
  } else if (originIsResizing(origin)) {
    flag |= UpdateMessageOrigin.Resizing;
  } else if (originIsAgent(origin)) {
    flag |= UpdateMessageOrigin.Agent;
  }

  return flag;
}

export const originatesFromUndoManager = (transaction: Transaction) =>
  originIsUndoManager(transaction.origin);

export const originatesFromPrivateSessionManager = (transaction: Transaction) =>
  isPrivateSessionManager(transaction.origin);

export const originatesFromAgent = (transaction: Transaction) =>
  isAgent(transaction.origin);

export const originatesFromLocalAgent = (transaction: Transaction) =>
  isAgent(transaction.origin) && transaction.local;

export class YjsOrigin implements YjsOriginInterface {
  constructor(
    public readonly remoteOrigin: TransactionOrigin,
    public readonly localOrigin: TransactionOrigin
  ) {}
}

export {
  CanvasTransactionOrigin,
  YJS_DEFAULT,
  type CanvasTransaction,
} from '@canvas/transaction-types';

export { TransactionOrigin, Transaction };
